/**
 * Persist the local player's last lobby position so a page refresh resumes
 * where they stood instead of teleporting back to the hall entrance.
 *
 * Only the horizontal location (x, z) is stored — y is always reset to the
 * spawn floor height on restore so a saved value can never drop the player
 * underground or strand them mid-air, and there's no vertical creep across
 * reloads (ecctrl's float-spring settles to a fixed ride height regardless of
 * the y it's dropped from). Values outside the hall footprint are rejected as
 * corrupt and fall back to the default spawn.
 */
import type { Vec3 } from "@solblox/shared";

const KEY = "solblox.lobby.spawn";

// Half-extent guard for the 28m × 34m hall (± a margin). A persisted position
// beyond this is treated as garbage rather than flinging the player into a wall.
const MAX_ABS = 40;

export interface SavedSpawn {
  x: number;
  z: number;
}

export function saveSpawn(s: SavedSpawn): void {
  if (!Number.isFinite(s.x) || !Number.isFinite(s.z)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ x: s.x, z: s.z }));
  } catch {
    // private mode / quota — position resume is best-effort.
  }
}

/**
 * Return the saved spawn merged onto `fallback` (which supplies the y floor),
 * or `fallback` unchanged when nothing valid is stored.
 */
export function loadSpawn(fallback: Vec3): Vec3 {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fallback;
    const v = JSON.parse(raw) as Partial<SavedSpawn>;
    if (
      typeof v.x !== "number" ||
      typeof v.z !== "number" ||
      !Number.isFinite(v.x) ||
      !Number.isFinite(v.z) ||
      Math.abs(v.x) > MAX_ABS ||
      Math.abs(v.z) > MAX_ABS
    ) {
      return fallback;
    }
    return { x: v.x, y: fallback.y, z: v.z };
  } catch {
    return fallback;
  }
}
